import { XMarkIcon } from "@heroicons/react/24/outline";
import { ReactNode, RefObject, useEffect, useState } from "react";

// A large dialog for "choose from a list, or click an item to look at it first".
// Both Fill Form pickers (Files, cases) use it. The host opens it the usual way
// (`modalRef.current.showModal()`).
//
//  - Nothing is mounted until the dialog opens, and everything is dropped when it
//    closes, so each opening starts fresh (no stale ticks, no needless fetches).
//  - The list stays mounted, just hidden, while an item is open, so ticks, the
//    search box and the page survive looking at an item and coming Back.
//  - Which item is open is local state, not a route, so the page behind the
//    dialog stays exactly where it was.
export interface ListDetailModalProps<T> {
  modalRef: RefObject<HTMLDialogElement>;
  title: string;
  renderList: (open: (item: T) => void) => ReactNode;
  renderDetail: (item: T, back: () => void) => ReactNode;
}

function useDialogOpen(ref: RefObject<HTMLDialogElement>) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    setIsOpen(dialog.hasAttribute("open"));
    const observer = new MutationObserver(() => setIsOpen(dialog.hasAttribute("open")));
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [ref]);
  return isOpen;
}

export default function ListDetailModal<T>({ modalRef, title, renderList, renderDetail }: ListDetailModalProps<T>) {
  const isOpen = useDialogOpen(modalRef);
  const [viewing, setViewing] = useState<T | null>(null);
  const back = () => setViewing(null);
  useEffect(() => {
    if (!isOpen) setViewing(null);
  }, [isOpen]);

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-6xl w-11/12 h-[88vh] p-0 flex flex-col overflow-hidden bg-base-100">
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-300 shrink-0">
          <h3 className="font-bold text-lg m-0">{title}</h3>
          <button type="button" aria-label="Close" onClick={() => modalRef.current?.close()}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {isOpen && (
          <>
            <div className={viewing === null ? "flex-1 min-h-0 overflow-auto flex flex-col" : "hidden"}>{renderList((item) => setViewing(item))}</div>
            {viewing !== null && <div className="flex-1 min-h-0 flex flex-col">{renderDetail(viewing, back)}</div>}
          </>
        )}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>
  );
}
